<template lang="pug">
div
  v-form(ref="form")
    div.d-flex.justify-center.mt-16(v-if="isCheckingPatient")
      v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
    template(v-else)
      v-row
        v-col(cols="8")
          v-row
            v-col(cols="6")
              div.text-sm.font-semibold.neutral700--text Fill in patient information below
              div.text-sm.font-medium.neutral700--text.mt-4 Patient image
              div.d-flex.align-center.mt-3
                div(style="width: 70px;")
                  v-responsive(:aspect-ratio="7/10")
                    v-card.fill-height.round-xl(v-if="resizeImageFileUrl" flat)
                      v-img(:src="resizeImageFileUrl" height="100%" width="100%")
                        template(v-slot:placeholder)
                          v-row(class="fill-height ma-0" align="center" justify="center")
                            v-progress-circular(indeterminate color="grey")
                    v-card.fill-height.round-xl(v-else outlined color="neutral100" style="border-color: var(--v-neutral300) !important;")
                      div.d-flex.align-center.justify-center.fill-height
                        v-icon(size="45") $yzi-user-03
                div.file-change-layout.ml-4
                  div(style="width: 120px;")
                    y-button(:label="resizeImageFileUrl ? 'Update image' : 'Add image'" size="xs" outlined block background-color="var(--v-neutral100)")
                  input.input-change-layout(type="file" @change="onFileChange" accept="image/*" style="width: 120px !important")

              div.text-sm.font-medium.neutral700--text.mt-4 {{ $t('name') }}
              v-text-field.text-sm.mt-2.round-xl(v-model="patient.name" :placeholder="$t('name')" outlined dense :rules="rules.not_empty" background-color="neutral100" hide-details="auto")
              div.text-sm.font-medium.neutral700--text.mt-4 {{ $t('nric_passport') }}
              v-text-field.text-sm.mt-2.round-xl(v-model="patient.ic" :placeholder="$t('nric_passport')" outlined dense :rules="fields.ic ? rules.not_empty : []" background-color="neutral100" hide-details="auto" @keyup="checkIC(patient.ic)")
              div.text-sm.font-medium.neutral700--text.mt-4 {{ $t('phone') }}
              phone-input.mt-2(v-model="patient.phone" required dense background-color="neutral100")
              div.text-sm.font-medium.neutral700--text.mt-4 {{ $t('email') }}
              v-text-field.text-sm.mt-2.round-xl(v-model="patient.email" :placeholder="$t('email')" outlined dense hide-details="auto" :rules="(fields.email || Boolean(patient.email)) ? rules.valid_email : []" background-color="neutral100")
              div.text-sm.font-medium.neutral700--text.mt-4 Date of birth
              v-text-field.text-sm.mt-2.round-xl(v-model="patient.dob" outlined dense hide-details="auto" type="date" :rules="fields.dob ? rules.not_empty : []" background-color="neutral100")
              div.text-sm.font-medium.neutral700--text.mt-4 Gender
              y-select.mt-2(v-model="patient.gender" :items="genders" item-text="text" item-value="value" :placeholder="$t('choose_gender')" :rules="fields.gender ? rules.not_empty_select : []" small background-color="neutral100")
              template(v-if="existingPatient")
                div.text-sm.font-medium.neutral700--text.mt-4 Family relation
                v-card.text-sm.round-xl.border-menu.mt-2(outlined :loading="isFetchingFamily" height="40")
                  div.d-flex.align-center.fill-height.px-3
                    v-icon.mr-2(size="16") $yzi-users-03
                    .text-sm(v-if="hasFamilyMember") {{ patient.relationships.length }} family {{ 'member' | pluralize({ count:patient.relationships.length }) }}
                    .text-sm(v-else) No family member
                    v-spacer
                    y-button(v-if="hasFamilyMember" label="View or edit" color="neutral400" background-color="var(--v-neutral300)" text-color="neutral700" size="xxs" height="24" outlined unset-min-width button-class="px-2" @click="handleClickFamilyRelation")
                    y-button(v-else label="Add now" color="neutral400" background-color="var(--v-neutral300)" text-color="neutral700" size="xxs" height="24" outlined unset-min-width button-class="px-2" @click="handleClickFamilyRelation")
              template(v-if="showStaffId")
                div.text-sm.font-medium.neutral700--text.mt-4 {{ $t('staffid') }}
                v-text-field.text-sm.mt-2.round-xl(v-model="patient.staff_id" :placeholder="$t('staffid')" outlined dense hide-details="auto" background-color="neutral100")

            v-col(cols="6")
              div.text-sm.font-medium.neutral700--text.mt-9 Nationality
              y-select.mt-2(v-model="patient.nationality" :items="countries" item-text="name" item-value="name" placeholder="Nationality" :rules="fields.nationality ? rules.not_empty_select : []" small background-color="neutral100")
              div.text-sm.font-medium.neutral700--text.mt-4 Race
              v-row.mt-2(no-gutters style="gap: 12px;")
                v-col.grow
                  y-select(v-model="patient.race" :items="races" item-text="text" item-value="value" placeholder="Race" :rules="fields.race ? rules.not_empty_select : []" small background-color="neutral100")
                v-col(v-if="patient.race === 'others'" cols="7")
                  v-text-field.text-sm.round-xl(v-model="otherRace" placeholder="Race" outlined dense hide-details="auto" :rules="fields.race ? rules.not_empty : []" background-color="neutral100")
              div.text-sm.font-medium.neutral700--text.mt-4 Religion
              v-row.mt-2(no-gutters style="gap: 12px;")
                v-col.grow
                  y-select(v-model="patient.religion" :items="religions" item-text="text" item-value="value" placeholder="Religion" :rules="fields.religion ? rules.not_empty_select : []" small background-color="neutral100")
                v-col(v-if="patient.religion === 'others'" cols="7")
                  v-text-field.text-sm.round-xl(v-model="otherReligion" placeholder="Religion" outlined dense hide-details="auto" :rules="fields.religion ? rules.not_empty : []" background-color="neutral100")
              .address
                div.text-sm.font-medium.neutral700--text.mt-4 Address
                v-text-field.text-sm.mt-2.round-xl(v-model="address.address_1" :placeholder="$t('address_1.label')" outlined dense :rules="(fields.address || !checkEmptyAddress(address)) ? rules.not_empty : []" background-color="neutral100" hide-details="auto")
                v-text-field.text-sm.mt-2.round-xl(v-model="address.address_2" :placeholder="$t('address_2.label')" outlined dense background-color="neutral100" hide-details="auto")
                v-text-field.text-sm.mt-2.round-xl(v-model="address.postcode" :placeholder="$t('postcode.label')" outlined dense :rules="(fields.address || !checkEmptyAddress(address)) ? rules.not_empty : []" background-color="neutral100" hide-details="auto" @keyup="fetchPostcodes(address.postcode, address, 'isFetchingAddress')")
                v-text-field.text-sm.mt-2.round-xl(v-model="address.city" :placeholder="$t('city.label')" outlined dense :rules="(fields.address || !checkEmptyAddress(address)) ? rules.not_empty : []" background-color="neutral100" hide-details="auto" :loading="isFetchingAddress")
                v-text-field.text-sm.mt-2.round-xl(v-model="address.state" :placeholder="$t('state.label')" outlined dense :rules="(fields.address || !checkEmptyAddress(address)) ? rules.not_empty : []" background-color="neutral100" hide-details="auto" :loading="isFetchingAddress")
                v-text-field.text-sm.mt-2.round-xl(v-model="address.country" :placeholder="$t('country.label')" outlined dense :rules="(fields.address || !checkEmptyAddress(address)) ? rules.not_empty : []" background-color="neutral100" hide-details="auto" :loading="isFetchingAddress")
              .emergency-contact
                div.text-sm.font-medium.neutral700--text.mt-4 Emergency contact name
                v-text-field.text-sm.mt-2.round-xl(v-model="patient.emergency_name" placeholder="Emergency contact name" outlined dense :rules="fields.emergency_contact ? rules.not_empty : []" background-color="neutral100" hide-details="auto")
                div.text-sm.font-medium.neutral700--text.mt-4 Emergency contact phone
                phone-input.mt-2(v-model="patient.emergency_phone" :required="fields.emergency_contact" dense background-color="neutral100")

          yz-divider.my-6

          // Panels section
          patient-panel-section.mt-6(:items.sync="staffIdItems")

          v-divider.my-6
          div
            div.text-sm.font-semibold.neutral700--text Medical information
            div.text-xs.neutral700--text Record patient's current medical details upon registration.
            underlying-conditions-section.mt-6(:items.sync="underlyingConditions")
            allergies-section.mt-6(:items.sync="allergies")
          br
        v-col(cols="4")
          div.d-flex.fill-height
            v-divider(vertical)
            div.pl-6(style="width: 100%;")
              div.text-sm.font-semibold.neutral700--text Additional info
              div.text-xs.neutral700--text This is the field that has been setup in your registration settings
              div(v-for="(field, cfindex) in customFields" :key="`cf-${cfindex}`")
                div.text-sm.font-medium.neutral700--text.mt-4 {{ field.extra_options.display_name || field.field_name }}
                template(v-if="field.datatype === 'string'")
                  v-text-field.text-sm.mt-2.round-xl(v-model="field.value" :placeholder="field.extra_options.placeholder || 'Enter text'" outlined dense :rules="field.extra_options.required ? rules.not_empty : []" background-color="neutral100" hide-details="auto")
                template(v-if="field.datatype === 'number'")
                  v-text-field.text-sm.mt-2.round-xl(v-model="field.value" :placeholder="field.extra_options.placeholder || 'Enter number'" outlined dense :rules="field.extra_options.required ? rules.not_empty : []" background-color="neutral100" hide-details="auto" type="number")
                template(v-if="field.datatype === 'text'")
                  v-textarea.text-sm.mt-2.round-xl(v-model="field.value" :placeholder="field.extra_options.placeholder || 'Enter text'" outlined :rules="field.extra_options.required ? rules.not_empty : []" background-color="neutral100" hide-details="auto" auto-grow rows="3")
                template(v-if="field.datatype === 'date'")
                  v-text-field.text-sm.mt-2.round-xl(v-model="field.value" outlined dense hide-details="auto" type="date" :rules="field.extra_options.required ? rules.not_empty : []" background-color="neutral100")
                template(v-if="field.datatype === 'phone'")
                  phone-input.mt-2(v-model="field.value" :required="field.extra_options.required" dense background-color="neutral100")
                template(v-if="field.datatype === 'email'")
                  v-text-field.text-sm.mt-2.round-xl(v-model="field.value" :placeholder="field.extra_options.placeholder || 'Enter email'" outlined dense :rules="field.extra_options.required ? rules.valid_email : []" background-color="neutral100" hide-details="auto")
                template(v-if="field.datatype === 'selection'")
                  //- radio button
                  template(v-if="field.extra_options.type === 'radiobtn'")
                    v-radio-group.mt-2(v-model="field.value" dense :rules="field.extra_options.required ? rules.not_empty : []" hide-details="auto")
                      template(v-for="(choice, cindex) in field.extra_options.choices")
                        v-radio(:value="choice" :key="`ch-${cfindex}-${cindex}`")
                          template(#label)
                            div.text-sm.font-normal.neutral700--text {{ choice }}
                      template(v-if="field.extra_options.enable_other")
                        v-radio(value="other_option")
                          template(#label)
                            div.text-sm.font-normal.neutral700--text Other
                        v-text-field.text-sm.round-xl.ml-8(v-if="field.value === 'other_option'" v-model="field.other_value" :placeholder="`Enter ${field.extra_options.display_name || field.field_name || 'option'}`" outlined dense :rules="field.extra_options.required ? rules.not_empty : []" background-color="neutral100" hide-details="auto")
                  //- checkbox
                  template(v-else-if="field.extra_options.type === 'checklist'")
                    template(v-for="(choice, cindex) in field.extra_options.choices")
                      v-checkbox(v-model="field.value" :value="choice" :key="`ch-${cfindex}-${cindex}`" on-icon="$yzi-check-square-solid" off-icon="$yzi-checkbox-uncheck" dense multiple hide-details="auto" :rules="field.extra_options.required ? rules.not_empty_array : []")
                        template(#label)
                          div.text-sm.font-normal.neutral700--text {{ choice }}
                    template(v-if="field.extra_options.enable_other")
                      v-checkbox(v-model="field.value" value="other_option" on-icon="$yzi-check-square-solid" off-icon="$yzi-checkbox-uncheck" dense multiple hide-details="auto" :rules="field.extra_options.required ? rules.not_empty_array : []")
                        template(#label)
                          div.text-sm.font-normal.neutral700--text Other
                      v-text-field.text-sm.round-xl.ml-8(v-if="field.value && field.value.includes('other_option')" v-model="field.other_value" :placeholder="`Enter ${field.extra_options.display_name || field.field_name || 'option'}`" outlined dense :rules="field.extra_options.required ? rules.not_empty : []" background-color="neutral100" hide-details="auto")
                template(v-if="field.datatype === 'address'")
                  .address
                    v-text-field.text-sm.mt-2.round-xl(v-model="field.value.address_1" :placeholder="$t('address_1.label')" outlined dense :rules="(field.extra_options.required || !checkEmptyAddress(field.value)) ? rules.not_empty : []" background-color="neutral100" hide-details="auto")
                    v-text-field.text-sm.mt-2.round-xl(v-model="field.value.address_2" :placeholder="$t('address_2.label')" outlined dense hide-details="auto")
                    v-text-field.text-sm.mt-2.round-xl(v-model="field.value.postcode" :placeholder="$t('postcode.label')" outlined dense :rules="(field.extra_options.required || !checkEmptyAddress(field.value)) ? rules.not_empty : []" background-color="neutral100" hide-details="auto" @keyup="fetchPostcodes(field.value.postcode, field.value, 'isFetchingCustomAddress')")
                    v-text-field.text-sm.mt-2.round-xl(v-model="field.value.city" :placeholder="$t('city.label')" outlined dense :rules="(field.extra_options.required || !checkEmptyAddress(field.value)) ? rules.not_empty : []" background-color="neutral100" hide-details="auto" :loading="isFetchingCustomAddress")
                    v-text-field.text-sm.mt-2.round-xl(v-model="field.value.state" :placeholder="$t('state.label')" outlined dense :rules="(field.extra_options.required || !checkEmptyAddress(field.value))? rules.not_empty : []" background-color="neutral100" hide-details="auto" :loading="isFetchingCustomAddress")
                    v-text-field.text-sm.mt-2.round-xl(v-model="field.value.country" :placeholder="$t('country.label')" outlined dense :rules="(field.extra_options.required || !checkEmptyAddress(field.value)) ? rules.not_empty : []" background-color="neutral100" hide-details="auto" :loading="isFetchingCustomAddress")

  .dialogs
    add-edit-family-dialog(v-if="showAddEditFamilyDialog" :open.sync="showAddEditFamilyDialog" :patientId="patient.id" :patientName="patient.name" @saved="handleFamilyUpdated")
    family-relation-dialog(v-if="showFamilyRelationDialog" :open.sync="showFamilyRelationDialog" :patientId="patient.id" @updated="handleFamilyUpdated")

</template>

<script>
import { resizeImage } from '@/utils'
import { mapGetters, mapActions } from 'vuex'
import { countries } from '@/assets/countries.js'
import PhoneInput from '@/components/clinic/custom/PhoneInput.vue'
import UnderlyingConditionsSection from '@/components/clinic/consultation/UnderlyingConditionsSection.vue'
import AllergiesSection from '@/components/clinic/consultation/AllergiesSection.vue'
import AddEditFamilyDialog from '@/components/clinic/relationship/AddEditFamilyDialog.vue'
import FamilyRelationDialog from '@/components/clinic/relationship/FamilyRelationDialog.vue'
import PanelDropdown from "@/components/clinic/panel/PanelDropdown.vue";
import PatientPanelSection from '@/components/clinic/panel/PatientPanelSection.vue';

export default {
  components: {PanelDropdown, PhoneInput, UnderlyingConditionsSection, AllergiesSection, AddEditFamilyDialog, FamilyRelationDialog, PatientPanelSection },
  props: {
    patientData: {
      type: Object,
      default: (() => {})
    },
    editMode: {
      type: Boolean,
      default: false
    },
  },
  data () {
    return {
      isCheckingPatient: false,
      isFetchingAddress: false,
      isFetchingCustomAddress: false,
      isFetchingFamily: false,
      showAddEditFamilyDialog: false,
      showFamilyRelationDialog: false,
      resizeImageFile: null,
      otherRace: null,
      otherReligion: null,
      resizeImageFileUrl: '',
      genders: [
        { text: 'Male', value: 'male' },
        { text: 'Female', value: 'female' },
      ],
      customFields: [],
      oriCustomFields: [],
      allergies: [],
      underlyingConditions: [],
      fields: {},
      patient: {},
      oriPatient: {},
      address: {},
      oriAddress: {},
      staffIdItems: [
        { panel: null, staffId: null }
      ],
      oriStaffIdItems: [],
      panelOptions: [
        { name: 'Panel A', value: 'panel_a' },
        { name: 'Panel B', value: 'panel_b' },
        { name: 'Panel C', value: 'panel_c' },
        { name: 'Panel D', value: 'panel_d' }
      ],
      rules: {
        not_empty: [v => (v != null && v.toString().trim().length) || 'This field is required'],
        has_quantity: [v => !!v || 'This field is required', v => v != 0 || 'This field is required'],
        has_price: [(v) => !!v || 'This field is required', (v) => v >= 1|| `Amount should be more than ${this.$currency.symbol} 1`],
        not_empty_select: [v => !!v || 'This field is required'],
        not_empty_array: [v => Array.isArray(v) || 'This field is required', v => (Array.isArray(v) && v.length > 0) || 'Please select at least one option' ],
        valid_email: [ v => /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(v) || 'E-mail must be valid', ]
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      clinic: 'clinic/getClinic',
      races: 'clinic/getRaces',
      religions: 'clinic/getReligions',
      custom_fields: 'clinic/getCustomFields',
    }),
    existingPatient () {
      return this.patientData && this.patientData.id
    },
    hasPatientData () {
      return this.patientData && Object.keys(this.patientData).length > 0
    },
    patientHasChanges () {
      if ((JSON.stringify(this.oriPatient) !== JSON.stringify(this.patient)) || (JSON.stringify(this.oriAddress) !== JSON.stringify(this.address)) || (JSON.stringify(this.oriStaffIdItems) !== JSON.stringify(this.staffIdItems)) || this.resizeImageFile) {
        return true
      }
      return false
    },
    customFieldsHasChanges () {
      if (JSON.stringify(this.oriCustomFields) !== JSON.stringify(this.customFields)) {
        return true
      }
      return false
    },
    hasFamilyMember () {
      return this.patient?.relationships?.length > 0
    },
    showStaffId () {
      if (this.company.genes.find(gene => gene.feature === 'show_staff_id_registration')) return true
      return false
    },
    countries () {
      return countries
    },
    hasActiveCustomFields () {
      return this.custom_fields.length > 0
    },
    defaultIC () {
      if (this.clinic && this.clinic.customer_uid_type === 'ic') {
        return true
      }
      return false
    },
  },
  mounted () {
    this.fetchFieldRules()
    this.checkPatient()
  },
  methods: {
    ...mapActions(['showMessage']),
    checkPatient () {
      if (this.hasPatientData) {
        if (this.existingPatient) {
          this.setPatientData(this.patientData, this.patientData.id)
        } else {
          this.isCheckingPatient = true
          let params = {}
          if (this.defaultIC) {
            params.ic = this.patientData.ic
          } else {
            params.phone = this.patientData.phone
          }
          this.$axios.get(`/clinics/${this.company.slug}/patients`, { params })
            .then(resp => {
              if (resp.data.results.length > 0) {
                this.setPatientData(resp.data.results[0], resp.data.results[0].id)
              } else {
                this.setPatientData(this.patientData)
              }
            })
            .catch(() => {
              this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
            })
            .finally(() => {
              this.isCheckingPatient = false
            })
        }
      } else {
        this.setCustomFields()
        this.setAllergiesConditions()
      }
    },
    async setPatientData (patientData, id) {
      let data = JSON.parse(JSON.stringify(patientData))
      if (data.race && !['melayu', 'cina', 'india', 'bumiputera'].includes(data.race)) {
        this.otherRace = data.race
        data.race = 'others'
      }
      if (data.religion && !['islam', 'buddha', 'hindu', 'kristian'].includes(data.religion)) {
        this.otherReligion = data.religion
        data.religion = 'others'
      }
      if (data.emergency_contact && Object.keys(data.emergency_contact).length > 0) {
        data.emergency_name = data.emergency_contact?.name || null
        data.emergency_phone = data.emergency_contact?.phone || null
      }
      if (data.address) {
        this.oriAddress = JSON.parse(JSON.stringify(data.address))
        this.address = JSON.parse(JSON.stringify(data.address))
      }
      delete data.address
      if (data.panels && data.panels.length > 0) {
        this.staffIdItems = data.panels.map(p => {
          return { panel: p.panel, staffId: p.staff_id }
        })
      }
      this.oriStaffIdItems = JSON.parse(JSON.stringify(this.staffIdItems))
      this.patient = JSON.parse(JSON.stringify(data))
      this.setAllergiesConditions(data)
      this.sanitizePhone()
      if (data.profile_image?.file) {
        this.resizeImageFileUrl = data.profile_image?.file
      } else if (data.photo) {
        this.resizeImageFileUrl = `data:image/jpeg;base64,${data.photo}`
      } else if (data.prefill_photo) {
        const base64 = `data:image/jpeg;base64,${data.prefill_photo}` 
        fetch(base64).then(resp => {
          if (resp) {
            resp.blob().then(blob => {
              const file = new File([blob], 'icphoto.jpeg')
              this.resizeImageFile = file
              this.resizeImageFileUrl = base64
            })
          }
        })
      }
      await new Promise(resolve => {
        if (data.custom_data.length) {
          let customData = JSON.parse(JSON.stringify(data.custom_data))
          this.setCustomFields(customData)
        } else {
          this.setCustomFields()
        }
        resolve()
      })
      if (id) {
        this.oriPatient = JSON.parse(JSON.stringify(data))
        if (!this.editMode) {
          this.$nextTick(() => {
            this.autoNext(id)
          })
        }
      }
    },
    setCustomFields (customData) {
      if (!this.hasActiveCustomFields) { return }
      let customFields = JSON.parse(JSON.stringify(this.custom_fields))
      let newFields = []
      if (customData) {
        customFields.forEach(cf => {
          cf.value = cf.datatype === 'address' ? {} : null
          let found = customData.find(cd => cd.id === cf.id)
          if (found) {
            if (cf.datatype === 'selection') {
              if (cf.extra_options.type === 'radiobtn') {
                found.value = found.value[0]
                if (!cf.extra_options.choices.includes(found.value)) {
                  found.other_value = found.value
                  found.value = 'other_option'
                }
              } else {
                found.value = found.value.map(m => {
                  if (!cf.extra_options.choices.includes(m)) {
                    found.other_value = m
                    return 'other_option'
                  }
                  return m
                })
              }
            }
            newFields.push({ ...cf, ...found })
          } else {
            newFields.push(cf)
          }
        })
      } else {
        newFields = customFields.map(m => { return { ...m, value: m.datatype === 'address' ? {} : null } })
      }
      this.customFields = JSON.parse(JSON.stringify(newFields))
      this.oriCustomFields = JSON.parse(JSON.stringify(newFields))
    },
    setAllergiesConditions (data) {
      if (data?.record) {
        if (data.record.allergies && data.record.allergies.length) {
          let allergies = data.record.allergies
          this.allergies = allergies.map(m => { return { ...m, editable: false } })
        } else {
          this.allergies.push({ name: null, reactions: null, editable: true })
        }
        if (data.record.underlying_conditions && data.record.underlying_conditions.length) {
          let conditions = data.record.underlying_conditions
          this.underlyingConditions = conditions.map(m => { return { ...m, editable: false } })
        } else {
          this.underlyingConditions.push({ name: null, remarks: null, editable: true })
        }
      } else {
        this.allergies.push({ name: null, reactions: null, editable: true })
        this.underlyingConditions.push({ name: null, remarks: null, editable: true })
      }
    },
    fetchFieldRules () {
      this.$axios.get(`/clinics/${this.company.slug}/registrations/form_fields`)
        .then(resp => {
          let fields = {}
          resp.data.forEach(o => {
            fields[o.field] = o.required
          })
          this.fields = fields
        })
    },
    fetchPostcodes (val, address, fetching) {
      if (!val) {
        return
      }
      if (val.length !== 5) {
        return
      }
      this[fetching] = true
      this.$axios.get(`/shipping/postcodes?postcode=${address.postcode}`).then((response) => {
        const addresses = response.data
        if (addresses.length > 0 && addresses[0].postcode) {
          this.$set(address, 'city', addresses[0].city)
          this.$set(address, 'state', addresses[0].state)
          this.$set(address, 'country', addresses[0].country)
        }
      })
      .catch(() => {})
      .finally(() => {
        this[fetching] = false
      })
    },
    autoNext (id) {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return
      }
      if (this.fields.image && !this.resizeImageFileUrl) {
        this.showMessage(['error', 'Patient image is required'])
        return
      }
      this.$emit('update', id)
    },
    async saveData () {
      if (!this.validateForm()) {
        return
      }
      this.$emit('loading', true)
      try {
        const patientData = this.preparePatientData()
        let patientId = this.existingPatient ? this.patient.id : null
        if (this.existingPatient) {
          await this.updateExistingPatient(patientData)
        } else {
          const response = await this.createNewPatient(patientData)
          patientId = response.data.id
        }
        await this.handleImageUpload(patientId)
        await this.handleCustomFields(patientId)
        await this.handleAllergiesConditions(patientId)
        this.$emit('update', patientId)
      } catch (err) {
        this.handleError(err)
      } finally {
        this.$emit('loading', false)
      }
    },
    validateForm() {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', this.$t('error_message.please_fill_out_all_required')])
        return false
      }
      if (this.fields.image && !this.resizeImageFileUrl) {
        this.showMessage(['error', 'Patient image is required'])
        return false
      }
      return true
    },
    preparePatientData() {
      let data = { 
        ...this.patient,
        ...(this.existingPatient ? {} : { source: 'admin' })
      }
      if (this.patient.religion === 'others') {
        data.religion = this.otherReligion
      }
      if (this.patient.race === 'others') {
        data.race = this.otherRace
      }
      if (this.patient.emergency_name || this.patient.emergency_phone) {
        data.emergency_contact = {
          name: this.patient.emergency_name || '',
          phone: this.patient.emergency_phone || ''
        }
      }
      if (!this.checkEmptyAddress(this.address)) {
        data.address = this.address
      }
      // Add panels data from staffIdItems
      if (this.staffIdItems && this.staffIdItems.length > 0) {
        data.panels = this.staffIdItems
          .filter(item => item.panel && item.staffId)
          .map(item => ({
            panel: item.panel.id,
            staff_id: item.staffId
          }))
      }
      delete data.profile_image
      return data
    },
    async updateExistingPatient(data) {
      if (this.patientHasChanges) {
        const endpoint = `/clinics/${this.company.slug}/patients/${this.patient.id}`
        const response = await this.$axios.patch(endpoint, data)
        this.updateActivityLog(endpoint, data, response, 'PATCH', this.patient.id, 'registration', 'update existing patient')
      }
    },
    async createNewPatient(data) {
      const endpoint = `/clinics/${this.company.slug}/patients`
      const response = await this.$axios.post(endpoint, data)
      this.updateActivityLog(endpoint, data, response, 'POST', response.data.id, 'registration', 'create new patient')
      return response
    },
    async handleImageUpload(patientId) {
      if (!this.resizeImageFile) return
      const formData = new FormData()
      formData.append('profile_image', this.resizeImageFile)
      try {
        await this.$axios.patch(`/clinics/${this.company.slug}/patients/${patientId}`, formData)
      } catch (err) {
        this.showMessage(['error', 'An error has occurred. Unable to update the image.'])
      }
    },
    async handleCustomFields(patientId) {
      if (!this.hasActiveCustomFields || (this.existingPatient && !this.customFieldsHasChanges)) return
      const customData = this.prepareCustomFieldsData()
      try {
        const endpoint = `/clinics/${this.company.slug}/patients/${patientId}/custom_data`
        const payload = { custom_data: customData }
        await this.$axios.post(endpoint, payload)
        // const response = await this.$axios.post(endpoint, payload)
        // this.updateActivityLog(payload, payload, response, 'POST', patientId, 'registration', 'update patient custom fields')
      } catch (err) {
        this.showMessage(['error', 'An error has occurred. Unable to update the custom fields.'])
      }
    },
    prepareCustomFieldsData() {
      return this.customFields.map(field => {
        let processedField = { ...field }
        if (field.datatype === 'selection') {
          if (field.extra_options.type === 'radiobtn') {
            if (field.value === 'other_option') {
              processedField.value = field.other_value
            }
            processedField.value = processedField.value ? [processedField.value] : []
          } else {
            if (field.value && field.value.includes('other_option')) {
              processedField.value = field.value.map(v => v === 'other_option' ? field.other_value : v)
            } else if (!field.value) {
              processedField.value = []
            }
          }
        }
        return processedField
      })
    },
    async handleAllergiesConditions(patientId) {
      if (!this.allergies.length && !this.underlyingConditions.length) return
      const allergies = this.allergies.filter(item => Boolean(item.name))
      const conditions = this.underlyingConditions.filter(item => Boolean(item.name))
      const payload = { allergies, underlying_conditions: conditions }
      try {
        const endpoint = `/clinics/${this.company.slug}/patients/${patientId}/record`
        await this.$axios.patch(endpoint, payload)
        // const response = await this.$axios.post(endpoint, { record })
        // this.updateActivityLog(endpoint, { record }, response, 'POST', patientId, 'registration', 'update patient allergies and conditions')
      } catch (err) {
        this.showMessage(['error', 'An error has occurred. Unable to update the patient record.'])
      }
    },
    handleError(err) {
      let message = this.$t('error_message.unable_to_complete_request')
      if (err.response?.data?.length > 0) {
        message = err.response.data[0]
      } else if (err.response?.data) {
        let data = JSON.stringify(err.response.data)
          .replace(/[^\w.\s:]/g, '')
          .replace(/[.:]/g, match => match + ' ')
        if (data) { message = data }
      }
      this.showMessage(['error', message])
    },
    handleFamilyUpdated () {
      this.isFetchingFamily = true
      this.$axios.get(`/clinics/${this.company.slug}/patients/${this.patient.id}`, { params: { fields: 'relationships' } })
        .then(resp => {
          this.$set(this.patient, 'relationships', resp.data.relationships)
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingFamily = false
        })
    },
    handleClickFamilyRelation () {
      if (this.hasFamilyMember) {
        this.showFamilyRelationDialog = true
      } else {
        this.showAddEditFamilyDialog = true
      }
    },
    checkIC (ic) {
      if (this.getDOBFromIC(ic)) {
        this.$set(this.patient, 'dob', this.getDOBFromIC(ic))
      }
    },
    checkEmptyAddress (address) {
      if (Object.keys(address).length === 0) {
        return true
      } else if (Object.values(address).every(val => !val)) {
        return true
      }
      return false
    },
    getDOBFromIC(icNumber) {
      // Removing non-digit characters from the IC number
      const cleanICNumber = icNumber.replace(/\D/g, '');
      
      if (cleanICNumber.length === 12 && /^[0-9]+$/.test(cleanICNumber)) {
        // Extracting birth date information
        const year = cleanICNumber.substring(0, 2);
        const month = cleanICNumber.substring(2, 4);
        const day = cleanICNumber.substring(4, 6);

        // Adjusting the year for the 21st century
        const currentYear = new Date().getFullYear();
        let century = 1900
        if (year <= Number(currentYear.toString().substring(2))) {
          century = 2000
        }
        const adjustedYear = parseInt(year, 10) + century;
        const finalYear = adjustedYear > currentYear ? adjustedYear - 100 : adjustedYear;

        // Formatting the date
        const formattedDOB = `${finalYear}-${month}-${day}`;
        const regex = /^(?:(?:19|20)\d\d)-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12][0-9]|3[01])$/
        if (regex.test(formattedDOB)) {
          return formattedDOB;
        }
      }
      return null
    },
    copyFields(newObj, existing) {
      for (let key in existing) {
        if (existing[key]) {
          newObj[key] = existing[key]
        }
      }
    },
    async onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      const resizedImage = await resizeImage(files[0], 'company_image.jpeg')
      this.resizeImageFileUrl = resizedImage.dataURL
      this.resizeImageFile = resizedImage.file
    },
    sanitizePhone () {
      if (this.patient.phone) {
        let phone = this.patient.phone.replace(/\D/g, '')
        this.$set(this.patient, 'phone', phone)
      }
    },
    updateActivityLog (endpoint, payload, response, method, id, ref, activity) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: ref,
        activity: activity,
        raw_data: { endpoint, payload, response: response.data, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
  }
}
</script>

<style lang="scss" scoped>
.file-change-layout {
  position: relative;
}
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
.border-menu {
  border: 1px solid var(--v-neutral400);
  box-shadow: none;
}
::v-deep {
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  } 
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }

}
</style>

<i18n>
{
  "en": {
    "address_1": {
      "label": "Address Line 1",
      "placeholder": "e.g. K-3-01, Conezion, Persiaran IRC3"
    },
    "address_2": {
      "label": "Address Line 2",
      "placeholder": "e.g. IOI Resort City"
    },
    "postcode": {
      "label": "Postcode",
      "placeholder": "e.g. 62502"
    },
    "city": {
      "label": "City",
      "placeholder": "e.g. Putrajaya"
    },
    "state": {
      "label": "State",
      "placeholder": "e.g. Wilayah Persekutuan Putrajaya"
    },
    "country": {
      "label": "Country",
      "placeholder": "e.g. Malaysia"
    }
  },
  "id": {
   "address_1": {
      "label": "Nama Jalan, Gedung, No Rumah, RT/RW, Kelurahan",
      "placeholder": "e.g. Jalan Prof. Joko Sutono Sh No.999, RT.14/RW.6, Petogogan, "
    },
    "address_2": {
      "label": "Kecamatan",
      "placeholder": "Kec. Kby. Baru"
    },
    "postcode": {
      "label": "Kode Pos",
      "placeholder": "12160"
    },
    "city": {
      "label": "Kota",
      "placeholder": "Kota Jakarta Selatan"
    },
    "state": {
      "label": "Provinsi",
      "placeholder": "DKI Jakarta"
    },
    "country": {
      "label": "Country",
      "placeholder": "e.g. Indonesia"
    }
  }
}
</i18n>
